import { type Set as ISet, Record } from '@gitbook/util-immutable';

import { MODEL_TYPES } from '../constants';
import type { Block, BlockCreateProps } from './block';
import type { BlockRangeCreateProps } from './block-range';
import type { Document } from './document';
import type { Fragment } from './fragment';
import type { Inline, InlineCreateProps } from './inline';
import type { Path } from './lookup-index';
import type { Mark, MarkCreateAttrs } from './mark';
import { createNodeProperties } from './node-properties';
import type { BlockJSON, InlineJSON, MarkJSON, TextJSON } from './node-serialization';
import type { Text, TextCreateProps } from './text';
import type { TextRangeCreateProps } from './text-range';
import type { SelectionRange } from './value';

const DEFAULTS: {
    length: undefined | number;
    mark: undefined | Mark;
    marks: undefined | ISet<Mark>;
    newPath: undefined | Path;
    node: undefined | Block | Inline | Text;
    offset: undefined | number;
    path: undefined | Path;
    fragmentPath: undefined | Path;
    fragmentIndex: undefined | number;
    position: undefined | number;
    properties: undefined | BlockCreateProps | InlineCreateProps | TextCreateProps;
    selection: undefined | SelectionRange;
    target: undefined | number | null;
    text: undefined | string;
    type: undefined | OperationType;
    data: undefined | any;
    dataPath: undefined | string[];
    dataIndex: undefined | number;
    previousData: undefined | any;
} = {
    length: undefined,
    mark: undefined,
    marks: undefined,
    newPath: undefined,
    node: undefined,
    offset: undefined,
    path: undefined,
    fragmentPath: undefined,
    fragmentIndex: undefined,
    position: undefined,
    properties: undefined,
    selection: undefined,
    target: undefined,
    text: undefined,
    type: undefined,
    data: undefined,
    dataPath: undefined,
    dataIndex: undefined,
    previousData: undefined,
};

/**
 * The side at which an operation must be inserted.
 * @description
 * Used when transforming two "insert_*" operations that are attempting to insert text or
 * nodes at the same offset. It becomes mandatory to know in which order they should be applied.
 */
export type OperationSide = 'left' | 'right';

export type OperationType =
    // Texts
    | 'insert_text'
    | 'remove_text'
    // Marks
    | 'add_mark'
    | 'remove_mark'
    // Nodes
    | 'insert_node'
    | 'remove_node'
    | 'move_node'
    | 'set_node'
    | 'split_node'
    | 'merge_node'
    // Fragments
    | 'insert_fragment'
    | 'set_fragment'
    | 'remove_fragment'
    // Data
    | 'insert_array_data'
    | 'set_data'
    | 'remove_array_data'
    | 'remove_data'
    // Selection
    | 'set_selection'
    // Only used to manually set the entire document value during dev
    | 'set_document';

/**
 * A node accepted as operation input: either a constructed model instance or
 * its serialized JSON form (both are handled downstream).
 */
type NodeInput = Block | Inline | Text | TextJSON | BlockJSON | InlineJSON;

/** A mark accepted as operation input: a model instance or a create/JSON form. */
type MarkInput = Mark | MarkJSON | MarkCreateAttrs;

/** A set of marks accepted as operation input. */
type MarksInput = ISet<Mark> | ReadonlyArray<MarkInput>;

export type InsertTextOperation = {
    type: 'insert_text';
    path: Path;
    offset: number;
    text: string;
    marks: MarksInput;
};

export type RemoveTextOperation = {
    type: 'remove_text';
    path: Path;
    offset: number;
    text: string;
    marks: MarksInput;
};

export type TextOperation = InsertTextOperation | RemoveTextOperation;

export type AddMarkOperation = {
    type: 'add_mark';
    path: Path;
    offset: number;
    length: number;
    mark: MarkInput;
};

export type RemoveMarkOperation = {
    type: 'remove_mark';
    path: Path;
    offset: number;
    length: number;
    mark: MarkInput;
};

export type MarkOperation = AddMarkOperation | RemoveMarkOperation;

export type InsertNodeOperation = {
    type: 'insert_node';
    path: Path;
    node: NodeInput;
};

export type RemoveNodeOperation = {
    type: 'remove_node';
    path: Path;
    node?: NodeInput;
};

export type MoveNodeOperation = {
    type: 'move_node';
    path: Path;
    newPath: Path;
};

export type SetNodeOperation = {
    type: 'set_node';
    path: Path;
    node?: NodeInput;
    properties: BlockCreateProps | InlineCreateProps | TextCreateProps;
};

export type MergeNodeOperation = {
    type: 'merge_node';
    path: Path;
    position: number;
    properties: BlockCreateProps | InlineCreateProps | TextCreateProps;
    target?: number | null;
};

export type SplitNodeOperation = {
    type: 'split_node';
    path: Path;
    position: number;
    properties: BlockCreateProps | InlineCreateProps | TextCreateProps;
    target?: number | null;
};

export type NodeOperation =
    | InsertNodeOperation
    | RemoveNodeOperation
    | MoveNodeOperation
    | SetNodeOperation
    | MergeNodeOperation
    | SplitNodeOperation;

export type InsertFragmentOperation = {
    type: 'insert_fragment';
    /** Path of the fragment's parent node */
    path: Path;
    /** Absolute path of the fragment. */
    fragmentPath: Path;
    node: Fragment;
    /** Index in the parent's fragments array to insert at. */
    fragmentIndex: number;
};

export type SetFragmentOperation = {
    type: 'set_fragment';
    /** Path of the fragment's parent node */
    path: Path;
    /** Absolute path of the fragment */
    fragmentPath: Path;
    node: Fragment;
    /** Index in the parent's fragments array to change */
    fragmentIndex: number;
};

export type RemoveFragmentOperation = {
    type: 'remove_fragment';
    /** Path of the fragment's parent node */
    path: Path;
    /** Absolute path of the fragment */
    fragmentPath: Path;
    node: Fragment;
    /** Index in the parent's fragments array to remove */
    fragmentIndex: number;
};

export type FragmentOperation =
    | InsertFragmentOperation
    | SetFragmentOperation
    | RemoveFragmentOperation;

export type SetDataOperation = {
    type: 'set_data';
    path: Path;
    dataPath: string[];
    data: any;
    previousData?: any;
};

export type RemoveDataOperation = {
    type: 'remove_data';
    path: Path;
    dataPath: string[];
    data: any;
    previousData?: any;
};

export type InsertArrayDataOperation = {
    type: 'insert_array_data';
    path: Path;
    dataPath: string[];
    data: any;
    dataIndex: number;
};

export type RemoveArrayDataOperation = {
    type: 'remove_array_data';
    path: Path;
    dataPath: string[];
    data: any;
    dataIndex?: number;
};

export type DataOperation =
    | SetDataOperation
    | RemoveDataOperation
    | InsertArrayDataOperation
    | RemoveArrayDataOperation;

export type SetSelectionOperation = {
    type: 'set_selection';
    selection: SelectionRange;
    properties: BlockRangeCreateProps | TextRangeCreateProps;
};

export type SetDocumentOperation = {
    type: 'set_document';
    data: Document;
};

export type AnyOperation =
    | TextOperation
    | MarkOperation
    | NodeOperation
    | FragmentOperation
    | DataOperation
    | SetSelectionOperation
    | SetDocumentOperation;

/**
 * Low-level operation on content.
 */
export class Operation extends Record(DEFAULTS) {
    // The backing Record stores every key from DEFAULTS, but each concrete
    // operation only populates the subset relevant to its `type`. Declare the
    // populated types here (mirroring how Block/Text expose their Record
    // fields) so type-specific access on a general `Operation` type-checks; the
    // transform logic guards on `type` before reading a given field.
    declare public type: OperationType;
    declare public readonly length: number;
    declare public readonly mark: Mark;
    declare public readonly marks: ISet<Mark>;
    declare public readonly newPath: Path;
    declare public readonly node: Block | Inline | Text | Fragment;
    declare public readonly offset: number;
    declare public readonly path: Path;
    declare public readonly fragmentPath: Path;
    declare public readonly fragmentIndex: number;
    declare public readonly position: number;
    declare public readonly properties: BlockCreateProps | InlineCreateProps | TextCreateProps;
    declare public readonly selection: SelectionRange;
    declare public readonly target: number | null;
    declare public readonly text: string;
    declare public readonly data: any;
    declare public readonly dataPath: string[];
    declare public readonly dataIndex: number;
    declare public readonly previousData: any;

    /**
     * Create an operation.
     *
     * The optional `object: 'operation'` marker is accepted (and ignored) so
     * callers can pass the fully-serialized operation shape, which mirrors the
     * `object` getter every operation exposes.
     */
    static create(input: AnyOperation & { object?: 'operation' }): Operation {
        // The input carries the loose create shape (serialized node/mark forms,
        // an optional `object` marker); createRecord only reads the stored
        // DEFAULTS keys, so the cast at that boundary is safe.
        type RecordInput = Parameters<typeof Operation.createRecord>[0];
        switch (input.type) {
            case 'merge_node':
            case 'split_node':
            case 'set_node':
                return Operation.createRecord({
                    ...input,
                    properties: createNodeProperties(
                        input.properties as BlockCreateProps | InlineCreateProps
                    ),
                } as RecordInput);
            default:
                return Operation.createRecord(input as RecordInput);
        }
    }

    /**
     * Check if `input` is a `Operation`.
     */
    public static isOperation(input: any): input is Operation {
        return Boolean(input?.[MODEL_TYPES.OPERATION]);
    }

    /*
     * Object.
     */
    get object(): 'operation' {
        return 'operation';
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Operation.prototype[MODEL_TYPES.OPERATION] = true;
